import { z } from 'zod';
import { organizationRefSchema } from './organizations';
import { pageSchema, timestampSchema, uuidSchema, versionedSchema } from './common';
import { entityRefSchema } from './entities';
import { linkedEntitySchema } from './links';

/**
 * Meetings, agendas and minutes (docs/meetings-design.md).
 *
 * The client asked that tasks, decisions and the rest be "objects that can be
 * edited in the minutes tool". The way that is built here is not a rich text
 * document with references embedded in the prose, but the other way round:
 *
 * > **An agenda item is a row, not a paragraph.**
 *
 * A meeting holds an ordered list of agenda items; each item has a number, a
 * heading, a body in the existing Markdown subset, and an ordered set of
 * *references* to objects elsewhere in the product. Rendered top to bottom that
 * *is* the memo -- the reader sees a document, the database sees rows, and
 * nobody has to convert between the two.
 *
 * The consequences are worth stating, because each one is a feature that a
 * document model would have made expensive:
 *
 *  - "Which meetings handled this task" is a join rather than a full-text scan
 *    over embedded nodes (see `meetingMentionSchema`).
 *  - Approval freezes a list rather than a blob, so there is nothing to diff.
 *  - Export has numbered sections, a decisions table and an actions table
 *    already, rather than having to recover them from prose.
 *
 * References point at any registered entity kind and are resolved through the
 * same registry cross-references use, so nothing in this module knows what a
 * schedule task is.
 */

/* --------------------------------------------------------------- lifecycle */

/**
 * Where a meeting is.
 *
 * Three states plus a terminal cancellation, rather than the five or six a
 * workflow diagram would suggest. An "agenda published" state was considered
 * and rejected: publishing an agenda is sending it to people, which is a
 * notification rather than a phase, and a state whose only effect is that a
 * button has been pressed teaches people to press it without reading.
 */
export const MEETING_STATES = ['planned', 'held', 'approved', 'cancelled'] as const;
export const meetingStateSchema = z.enum(MEETING_STATES);
export type MeetingState = (typeof MEETING_STATES)[number];

/**
 * The legal moves, as data.
 *
 * `approved` and `cancelled` are terminal. Re-opening an approved minute is
 * deliberately not a transition: the minutes are a record by then, and the way
 * to correct a record is an attributed correction appended to it, not an edit
 * that leaves no trace. That is the same reasoning that makes the audit table
 * append-only at the grant level.
 */
const MEETING_TRANSITIONS: Record<MeetingState, readonly MeetingState[]> = {
  planned: ['held', 'cancelled'],
  held: ['approved', 'cancelled'],
  approved: [],
  cancelled: [],
};

export function canTransitionMeeting(from: MeetingState, to: MeetingState): boolean {
  return MEETING_TRANSITIONS[from].includes(to);
}

export function nextMeetingStates(from: MeetingState): readonly MeetingState[] {
  return MEETING_TRANSITIONS[from];
}

/**
 * Whether the agenda and its prose may still be written.
 *
 * True in `planned` and `held`, which is the answer that surprises people:
 * writing the minutes *is* editing agenda items, and items are still added
 * during a meeting because things come up. What `approved` stops is exactly
 * this, and nothing else -- the objects an item references go on living. See
 * section 9 of the design: the prose is frozen, the objects are live.
 */
export function isMeetingEditable(state: MeetingState): boolean {
  return state === 'planned' || state === 'held';
}

/* ------------------------------------------------------------ participants */

export const MEETING_PARTICIPANT_ROLES = [
  'chair',
  'secretary',
  'participant',
  /** Receives the minutes without attending. Every real minutes template has
   *  this, and it is otherwise a sentence at the bottom that no query can
   *  read. */
  'distribution',
] as const;
export const meetingParticipantRoleSchema = z.enum(MEETING_PARTICIPANT_ROLES);
export type MeetingParticipantRole = (typeof MEETING_PARTICIPANT_ROLES)[number];

export const MEETING_ATTENDANCE = ['present', 'absent', 'partial'] as const;
export const meetingAttendanceSchema = z.enum(MEETING_ATTENDANCE);
export type MeetingAttendance = (typeof MEETING_ATTENDANCE)[number];

/**
 * Somebody at the meeting, with or without an account.
 *
 * The nullable `userId` beside a free-text name is not a shortcut. Site
 * meetings are attended by the contractor's foreman and the client's inspector,
 * neither of whom has a login here, and minutes that cannot name them are
 * minutes nobody can use. Exactly one of the two is set, which the service
 * enforces and a check constraint backs.
 *
 * When the person *does* have an account the row points at it, and that is what
 * lets "your actions from Tuesday's meeting" work later.
 */
export const meetingParticipantSchema = z.object({
  id: uuidSchema,
  userId: uuidSchema.nullable(),
  /** Set when `userId` is null. Null otherwise -- the display name comes from
   *  the account, so storing it twice would let the two drift. */
  externalName: z.string().nullable(),
  externalOrganization: z.string().nullable(),
  /**
   * The organization the attendee represented, from the register
   * (docs/organizations-design.md section 9.1): an account holder's membership
   * when the minutes were saved, or the organization an external attendee was
   * recorded against. `externalOrganization` keeps the name as written.
   */
  organization: organizationRefSchema.nullable(),
  role: meetingParticipantRoleSchema,
  attendance: meetingAttendanceSchema,
});
export type MeetingParticipant = z.infer<typeof meetingParticipantSchema>;

export const MAX_MEETING_PARTICIPANTS = 100;

export const meetingParticipantInputSchema = z
  .object({
    userId: uuidSchema.nullish(),
    externalName: z.string().min(1).max(200).nullish(),
    externalOrganization: z.string().max(200).nullish(),
    /** For an external attendee: the organization they came from, when it is
     *  in the register. An account holder's is their membership, and is not
     *  sent. */
    partyId: uuidSchema.nullish(),
    role: meetingParticipantRoleSchema.default('participant'),
    attendance: meetingAttendanceSchema.default('present'),
  })
  .strict()
  .refine((value) => Boolean(value.userId) !== Boolean(value.externalName), {
    message: 'a participant is either an account or a name, never both and never neither',
    path: ['externalName'] as PropertyKey[],
  });
export type MeetingParticipantInput = z.infer<typeof meetingParticipantInputSchema>;

/* --------------------------------------------------------------- reference */

/**
 * Why an object is on an agenda item.
 *
 * Two values. `reviewed` -- brought to the meeting to be discussed;
 * `produced` -- created during it as an outcome. That is the distinction the
 * rendered minutes need, since "items handled" and "decisions and actions" are
 * different sections, and it is the distinction "which meeting created this
 * action?" is asking about.
 *
 * A longer vocabulary -- for information, for decision, for approval, for
 * noting -- was considered and left to the item's own text, on the same grounds
 * the link relations are a short list: a taxonomy nobody can hold in their head
 * produces values chosen at random, which is worse for finding things than no
 * classification at all.
 */
export const AGENDA_REFERENCE_ROLES = ['reviewed', 'produced'] as const;
export const agendaReferenceRoleSchema = z.enum(AGENDA_REFERENCE_ROLES);
export type AgendaReferenceRole = (typeof AGENDA_REFERENCE_ROLES)[number];

export const MAX_AGENDA_REFERENCE_NOTE_LENGTH = 500;

/**
 * One object on the agenda, resolved.
 *
 * `target` is the same `LinkedEntity` shape the cross-reference list uses, and
 * for the same reason: everything needed to recognise the item and go to it,
 * produced by the module that owns it, with status as a translation key rather
 * than a sentence because the backend does not speak the reader's language.
 */
export const agendaReferenceSchema = z.object({
  id: uuidSchema,
  role: agendaReferenceRoleSchema,
  note: z.string().nullable(),
  orderIndex: z.number().int(),
  target: linkedEntitySchema,
});
export type AgendaReference = z.infer<typeof agendaReferenceSchema>;

export const createAgendaReferenceSchema = z
  .object({
    entity: entityRefSchema,
    role: agendaReferenceRoleSchema.default('reviewed'),
    note: z.string().max(MAX_AGENDA_REFERENCE_NOTE_LENGTH).nullish(),
  })
  .strict();
export type CreateAgendaReference = z.infer<typeof createAgendaReferenceSchema>;

export const updateAgendaReferenceSchema = z
  .object({
    role: agendaReferenceRoleSchema.optional(),
    note: z.string().max(MAX_AGENDA_REFERENCE_NOTE_LENGTH).nullish(),
    orderIndex: z.number().int().min(0).max(9999).optional(),
  })
  .strict();
export type UpdateAgendaReference = z.infer<typeof updateAgendaReferenceSchema>;

/* ------------------------------------------------------------ agenda items */

export const MAX_AGENDA_ITEMS_PER_MEETING = 200;
export const MAX_REFERENCES_PER_AGENDA_ITEM = 50;
export const MAX_AGENDA_BODY_LENGTH = 20000;

export const agendaItemSchema = z.object({
  id: uuidSchema,
  parentId: uuidSchema.nullable(),
  orderIndex: z.number().int(),
  /** "5.2". Derived from position rather than stored -- see
   *  `numberAgendaItems`. */
  number: z.string(),
  title: z.string(),
  /** The Markdown subset the dashboard already renders. Null while the agenda
   *  is being planned and nothing has been said yet. */
  body: z.string().nullable(),
  /** The item this one was carried forward from, when it was. */
  carriedFromItemId: uuidSchema.nullable(),
  references: z.array(agendaReferenceSchema),
  version: z.number().int().nonnegative(),
});
export type AgendaItem = z.infer<typeof agendaItemSchema>;

/**
 * The displayed numbering, derived from position.
 *
 * Storing the number would mean renumbering every following row on an insert,
 * and a stored number that disagrees with its position is the kind of bug that
 * survives a release because it looks like a typo.
 *
 * Two robustness rules, both about data the validation should already have
 * prevented and neither of which may lose a row on screen: an item whose parent
 * is not itself a top-level item is numbered as though it were top-level, and
 * ties on `orderIndex` are broken by id so the numbering is at least stable
 * between two reads.
 */
export function numberAgendaItems(
  items: readonly { id: string; parentId: string | null; orderIndex: number }[],
): Map<string, string> {
  type Item = { id: string; parentId: string | null; orderIndex: number };

  /* `orderIndex` is sibling-scoped, not global: item 1's second child and item
   * 2 both legitimately hold index 20. So the levels are partitioned before
   * anything is sorted -- ordering the whole list at once lets a child sort
   * ahead of a top-level item and take its number. */
  const bySiblingOrder = (a: Item, b: Item) =>
    a.orderIndex - b.orderIndex || (a.id < b.id ? -1 : a.id > b.id ? 1 : 0);

  const topLevelIds = new Set(
    items.filter((item) => item.parentId === null).map((item) => item.id),
  );
  /* An item whose parent is not itself top-level is numbered as though it were
   * top-level. That is robustness rather than a supported shape -- the service
   * refuses a second level of nesting -- and the rule is chosen so a stray row
   * appears in the wrong place rather than not at all. */
  const isTopLevel = (item: Item) => item.parentId === null || !topLevelIds.has(item.parentId);

  const roots: Item[] = [];
  const childrenByParent = new Map<string, Item[]>();
  for (const item of items) {
    if (isTopLevel(item)) {
      roots.push(item);
      continue;
    }
    const parentId = item.parentId as string;
    childrenByParent.set(parentId, [...(childrenByParent.get(parentId) ?? []), item]);
  }

  const numbers = new Map<string, string>();
  roots.sort(bySiblingOrder);

  roots.forEach((root, index) => {
    const rootNumber = String(index + 1);
    numbers.set(root.id, rootNumber);

    const children = (childrenByParent.get(root.id) ?? []).sort(bySiblingOrder);
    children.forEach((child, childIndex) => {
      numbers.set(child.id, `${rootNumber}.${childIndex + 1}`);
    });
  });

  return numbers;
}

export const createAgendaItemSchema = z
  .object({
    title: z.string().min(1).max(300),
    body: z.string().max(MAX_AGENDA_BODY_LENGTH).nullish(),
    /** One level of nesting only, enforced in the service: agendas nest once in
     *  practice, and unlimited depth buys an outliner nobody asked for at the
     *  cost of a recursive read path. */
    parentId: uuidSchema.nullish(),
    /** Insert after this sibling; omitted means append. Expressed as a
     *  neighbour rather than an index so two clients adding at once cannot both
     *  claim position 3. */
    afterItemId: uuidSchema.nullish(),
    /**
     * Objects to put on the item in the same request.
     *
     * This is what makes the drag gesture one round trip: dropping a task on
     * the agenda creates the item and its reference together, and a failure
     * leaves neither.
     */
    references: z.array(createAgendaReferenceSchema).max(MAX_REFERENCES_PER_AGENDA_ITEM).optional(),
  })
  .strict();
export type CreateAgendaItem = z.infer<typeof createAgendaItemSchema>;

export const updateAgendaItemSchema = z
  .object({
    title: z.string().min(1).max(300).optional(),
    body: z.string().max(MAX_AGENDA_BODY_LENGTH).nullish(),
  })
  .extend(versionedSchema.shape)
  .strict();
export type UpdateAgendaItem = z.infer<typeof updateAgendaItemSchema>;

export const moveAgendaItemSchema = z
  .object({
    /** The new parent, or null for top level. Omitted leaves it where it is. */
    parentId: uuidSchema.nullish(),
    /** The sibling to sit after, or null for first. */
    afterItemId: uuidSchema.nullish(),
  })
  .strict();
export type MoveAgendaItem = z.infer<typeof moveAgendaItemSchema>;

/* ---------------------------------------------------------------- meetings */

export const meetingSummarySchema = z.object({
  id: uuidSchema,
  projectId: uuidSchema,
  title: z.string(),
  state: meetingStateSchema,
  /** The day it is held. Date rather than timestamp: a meeting is an entry in a
   *  diary, and the start time is a detail people change twice before it
   *  happens. `startsAt` carries the time when there is one. */
  heldOn: z.string(),
  startsAt: timestampSchema.nullable(),
  location: z.string().nullable(),
  /** Who called the meeting. A property of the meeting rather than a
   *  participant role -- see the schema comment for why. */
  organizerUserId: uuidSchema.nullable(),
  agendaItemCount: z.number().int().nonnegative(),
  participantCount: z.number().int().nonnegative(),
  version: z.number().int().nonnegative(),
  createdAt: timestampSchema,
  updatedAt: timestampSchema,
});
export type MeetingSummary = z.infer<typeof meetingSummarySchema>;

export const meetingSummaryPageSchema = pageSchema(meetingSummarySchema);

export const meetingSchema = meetingSummarySchema.extend({
  /** Free-text preamble: what the meeting is for. Markdown subset. */
  purpose: z.string().nullable(),
  participants: z.array(meetingParticipantSchema),
  agenda: z.array(agendaItemSchema),
  approvedAt: timestampSchema.nullable(),
  approvedByUserId: uuidSchema.nullable(),
  createdByUserId: uuidSchema.nullable(),
  /** The meeting this one carried its unfinished items from, when it did. */
  carriedFromMeetingId: uuidSchema.nullable(),
});
export type Meeting = z.infer<typeof meetingSchema>;

const isoDateSchema = z.string().date();

export const createMeetingSchema = z
  .object({
    title: z.string().min(1).max(300),
    heldOn: isoDateSchema,
    startsAt: timestampSchema.nullish(),
    location: z.string().max(300).nullish(),
    purpose: z.string().max(MAX_AGENDA_BODY_LENGTH).nullish(),
    organizerUserId: uuidSchema.nullish(),
    participants: z.array(meetingParticipantInputSchema).max(MAX_MEETING_PARTICIPANTS).optional(),
  })
  .strict();
export type CreateMeeting = z.infer<typeof createMeetingSchema>;

export const updateMeetingSchema = z
  .object({
    title: z.string().min(1).max(300).optional(),
    heldOn: isoDateSchema.optional(),
    startsAt: timestampSchema.nullish(),
    location: z.string().max(300).nullish(),
    purpose: z.string().max(MAX_AGENDA_BODY_LENGTH).nullish(),
    organizerUserId: uuidSchema.nullish(),
    /** Sent whole rather than patched member by member. A participant list is
     *  short, it is edited as a list on screen, and a diff protocol over it
     *  would be more machinery than the thing it manages. */
    participants: z.array(meetingParticipantInputSchema).max(MAX_MEETING_PARTICIPANTS).optional(),
  })
  .extend(versionedSchema.shape)
  .strict();
export type UpdateMeeting = z.infer<typeof updateMeetingSchema>;

export const meetingStateChangeSchema = z
  .object({ state: meetingStateSchema })
  .extend(versionedSchema.shape)
  .strict();
export type MeetingStateChange = z.infer<typeof meetingStateChangeSchema>;

/**
 * Which end of the calendar the page starts from.
 *
 * `desc` -- most recent first -- is the default because a meetings list is read
 * from the top by somebody looking for last week's minutes. `asc` exists for
 * the one caller that asks the opposite question: "what is coming up?". Without
 * it that caller has to fetch a page ordered away from what it wants and filter
 * in the browser, which is correct only while the whole project fits on one
 * page and silently wrong afterwards.
 */
export const meetingOrderSchema = z.enum(['asc', 'desc']);
export type MeetingOrder = z.infer<typeof meetingOrderSchema>;

export const meetingListQuerySchema = z
  .object({
    state: meetingStateSchema.optional(),
    from: isoDateSchema.optional(),
    to: isoDateSchema.optional(),
    search: z.string().max(120).optional(),
    order: meetingOrderSchema.default('desc'),
    cursor: z.string().max(256).optional(),
    limit: z.coerce.number().int().min(1).max(100).default(25),
  })
  .strict();
export type MeetingListQuery = z.infer<typeof meetingListQuerySchema>;

/* ---------------------------------------------------------- carry forward */

/**
 * Starting the next meeting with the unfinished business of the last one.
 *
 * The single most valuable thing a meeting tool does, and the part of
 * recurrence that carries the value -- which is why a true recurring series,
 * generating instances on a cadence, is deliberately not built. Items are
 * copied with their `reviewed` references and a `carriedFromItemId` back
 * pointer, so "this has been on the agenda four times running" stays
 * answerable.
 *
 * `produced` references are not copied: a decision taken last time is a record
 * of that meeting, and carrying it forward would make it look like it was taken
 * again.
 */
export const carryForwardSchema = z
  .object({
    /** The meeting to take items from. Named explicitly rather than inferred as
     *  "the previous one", because the previous one is sometimes not the one
     *  this meeting continues. */
    sourceMeetingId: uuidSchema,
    /** Which items to bring. Empty means every item whose referenced objects
     *  are not all finished. */
    itemIds: z.array(uuidSchema).max(MAX_AGENDA_ITEMS_PER_MEETING).optional(),
  })
  .strict();
export type CarryForward = z.infer<typeof carryForwardSchema>;

/* ---------------------------------------------------------------- mentions */

/**
 * "Where has this been discussed?" -- the client's fourth wish.
 *
 * Its own read model rather than a derived cross-reference, for three reasons
 * the generic links panel cannot meet: it is ordered by meeting date, it is
 * bounded by recency, and it carries what each meeting *produced* under that
 * item. A task on a three-year project appears in forty fortnightly meetings,
 * and a links panel capped at 200 and grouped by kind is not where forty of
 * them belong.
 */
export const MEETING_MENTION_LIMIT_MAX = 50;

export const meetingMentionSchema = z.object({
  meetingId: uuidSchema,
  meetingTitle: z.string(),
  heldOn: z.string(),
  state: meetingStateSchema,
  itemId: uuidSchema,
  itemNumber: z.string(),
  itemTitle: z.string(),
  role: agendaReferenceRoleSchema,
  /** What the meeting produced under this item, resolved. Empty for most
   *  mentions; the ones that are not empty are the ones a reader is looking
   *  for. */
  outcomes: z.array(linkedEntitySchema),
});
export type MeetingMention = z.infer<typeof meetingMentionSchema>;

export const meetingMentionListSchema = z.object({
  anchor: entityRefSchema,
  mentions: z.array(meetingMentionSchema),
  /** How many more exist beyond the limit. Stated rather than implied, so a
   *  reader of a truncated list knows it is one. */
  remaining: z.number().int().nonnegative(),
});
export type MeetingMentionList = z.infer<typeof meetingMentionListSchema>;

export const meetingMentionQuerySchema = z
  .object({
    kind: entityRefSchema.shape.kind,
    id: uuidSchema,
    limit: z.coerce.number().int().min(1).max(MEETING_MENTION_LIMIT_MAX).default(10),
  })
  .strict();
export type MeetingMentionQuery = z.infer<typeof meetingMentionQuerySchema>;
